import React, { useEffect, useState, useRef } from 'react';
import { useService } from '@Shared.Angular/flowingly.hooks/useService';
import { SharedAngular } from '@Angular.Runner/@types/sharedAngular';
import Modal from '@Shared.Angular/flowingly.components/Modal/Modal';

const SessionTimeoutNotifier: React.FC = () => {
  const appConfig = useService<SharedAngular.APP_CONFIG>('APP_CONFIG');
  const pubSubService =
    useService<SharedAngular.PubSubService>('pubsubService');

  if (!appConfig.enableSessionTimeoutReminder) {
    return null;
  }

  if (
    appConfig.sessionTimeoutReminderTriggerInSecond >=
    appConfig.sessionTimeoutInSecond
  ) {
    console.warn(
      'Invalid config: sessionTimeoutReminderTriggerInSecond should be less than sessionTimeoutInSecond'
    );
    return null;
  }

  const [showPopup, setShowPopup] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);

  const expiresAtRef = useRef<number | null>(null);
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownTimerStarterRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const subscriberId = 'session-timeout-notifier';

  useEffect(() => {
    const handleBeforeUnload = () => {
      clearTimers();
      setShowPopup(false);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    pubSubService.subscribe(
      'IDLE_EXPIRY_CHANGED',
      (_event, expiryDateTime: string) =>
        handleIdleExpiryChange(expiryDateTime),
      subscriberId
    );

    return () => {
      clearTimers();
      pubSubService.unsubscribeAll(subscriberId);
    };
  }, []);

  const handleIdleExpiryChange = (expiryDateTime: string) => {
    setShowPopup(false);
    clearTimers();
    const expiryTimestamp = new Date(expiryDateTime).getTime();
    if (isNaN(expiryTimestamp)) {
      console.warn('Invalid expiryDateTime received in sessionTimeoutNotifier');
      return;
    }
    expiresAtRef.current = expiryTimestamp;
    schedulePopup();
  };

  const schedulePopup = () => {
    const reminderTriggerInSecond =
      appConfig.sessionTimeoutReminderTriggerInSecond;
    const warningTimeInMilliSeconds = reminderTriggerInSecond * 1000;
    const timeLeft = expiresAtRef.current - Date.now();

    if (timeLeft <= warningTimeInMilliSeconds) {
      startCountdown(Math.ceil(timeLeft / 1000));
    } else {
      countdownTimerStarterRef.current = setTimeout(
        () => startCountdown(reminderTriggerInSecond),
        timeLeft - warningTimeInMilliSeconds
      );
    }
  };

  const startCountdown = (seconds: number) => {
    setShowPopup(true);
    setCountdown(seconds);

    countdownTimerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearTimers();
          setShowPopup(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const clearTimers = () => {
    clearInterval(countdownTimerRef.current);
    clearTimeout(countdownTimerStarterRef.current);
  };

  const formatTime = (countdown: number | null): string => {
    const pad = (num: number) => String(num).padStart(2, '0');

    const hours = Math.floor(countdown / 3600);
    const minutes = Math.floor((countdown % 3600) / 60);
    const seconds = countdown % 60;

    return hours > 0
      ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
      : `${pad(minutes)}:${pad(seconds)}`;
  };

  return (
    showPopup && (
      <Modal
        hideActionButton={true}
        hideCancelButton={true}
        onClose={() => setShowPopup(false)}
      >
        <div className="session-notify-content">
          <p className="session-notify-title">Your session ends in</p>
          <p className="session-notify-timer">{formatTime(countdown)}</p>
          <p className="session-notify-warning-text">
            Unsaved changes may be lost! <br />
            To stay logged in, interact with the <br />
            application before your session <br />
            expires.
          </p>
        </div>
      </Modal>
    )
  );
};

export default SessionTimeoutNotifier;
